import { useCallback, useMemo, useRef } from 'react';

// MD or an explicit switch can report a patient before Heidi has navigated to
// that patient's session. We hold that patient as authoritative so we don't
// reopen a stale route patient while the new session is still being created
// or navigated to.
export type PendingMdPatient = {
  get: () => string | null;
  set: (patientId: string) => void;
  clear: () => void;
  clearIfMatches: (patientId: string) => void;
  captureInitial: (readInitialPatientId: () => string | null) => void;
};

export const usePendingMdPatient = (): PendingMdPatient => {
  const pendingRef = useRef<string | null>(null);
  const hasCapturedInitialRef = useRef(false);

  const get = useCallback(() => pendingRef.current, []);

  const set = useCallback((patientId: string) => {
    pendingRef.current = patientId;
  }, []);

  const clear = useCallback(() => {
    pendingRef.current = null;
  }, []);

  const clearIfMatches = useCallback((patientId: string) => {
    if (pendingRef.current === patientId) {
      pendingRef.current = null;
    }
  }, []);

  const captureInitial = useCallback((readInitialPatientId: () => string | null) => {
    if (hasCapturedInitialRef.current) return;
    hasCapturedInitialRef.current = true;

    const initial = readInitialPatientId();
    if (initial) {
      pendingRef.current = initial;
    }
  }, []);

  // Memoise so the returned object is referentially stable. Consumers list
  // `pendingPatient` in effect/callback dep arrays; a fresh literal every
  // render would re-fire those effects on every parent render.
  return useMemo(
    () => ({ get, set, clear, clearIfMatches, captureInitial }),
    [get, set, clear, clearIfMatches, captureInitial]
  );
};
